import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'
import { Moment } from 'moment'
import moment = require('moment')

import { KeyTransformer } from 'util/key-transformer'
import { BaseModelService } from './base-model.service'
import { OnlineSession } from './online-session.model'
import { User } from "./user.model"
import { OnlineSessionSerializer, UserSerializer } from 'serializers'

export enum SmsType { interview, briefing }
export interface ISmsSendSign {
  need: boolean
  type: SmsType
}
@Injectable()
export class OnlineSessionService extends BaseModelService<OnlineSession> {
  constructor() {
    super()

    this.configure({
      basePath:  'online_learning_sessions',
      key:       'session',
      pluralKey: 'sessions',
      serializer: OnlineSessionSerializer
    })
  }

  interviewAccept(s: OnlineSession, interviewData: Object):  Observable<Object> {
    let url = this.buildUrl(s.uuid, 'interviewer_accept')
    let params = {}
    params["interview"] = KeyTransformer.underscore(interviewData)

    let body = JSON.stringify(params)
    return this.appHttp.patch(url, body)
      .map((response) => this.processResponse(response))
  }

  interviewReject(s: OnlineSession, interviewData: Object):  Observable<Object> {
    let url = this.buildUrl(s.uuid, 'interviewer_reject')
    let params = {}
    params["interview"] = KeyTransformer.underscore(interviewData)

    let body = JSON.stringify(params)
    return this.appHttp.patch(url, body)
      .map((response) => this.processResponse(response))
  }
  
  
  interviewerComplete(s: OnlineSession, interviewData: Object): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'interviewer_complete')
    let params = {}
    params["interview"] = KeyTransformer.underscore(interviewData)

    let body = JSON.stringify(params)
    return this.appHttp.patch(url, body)
      .map((response) => this.processResponse(response))
  }

  interviewAdminComplete(s: OnlineSession, interviewData: Object): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'interview_admin_complete')
    let params = {}
    params["interview"] = KeyTransformer.underscore(interviewData)
    
    let body = JSON.stringify(params)
    return this.appHttp.patch(url, body)
      .map((response) => this.processResponse(response))
  }

  intervieverAssign(s: OnlineSession, interviewerId: number): Observable<OnlineSession> {
    const url = this.buildUrl(s.uuid, 'interviewer_assign')
    const body = JSON.stringify({ interviewer_id: interviewerId })
  
    return this.appHttp.patch(url, body)
      .map(resp => this.processResponse(resp))
      .map(data => data["session"] as OnlineSession)
  }

  intervieverUnassign(s: OnlineSession): Observable<OnlineSession> {
    let url = this.buildUrl(s.uuid, 'interviewer_unassign')
    return this.patch(url).map(data => data["session"] as OnlineSession)
  }

  /**
   * Обновляет время ткущей записи на собедование
   * @param s - сессия, для которой изменяется время интервью
   * @param newTime - unix timestamp, время на которое переносится интервью
   */
  moveInterviewRegistration(s: OnlineSession, newTime: number): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'move_interview_registration')
    let body = JSON.stringify({
      interview_registration: {
        time: newTime
      }
    })
    return this.appHttp.patch(url, body).map((response) => this.processResponse(response))
  }

  /**
   * Добавляет к сессии комментарий интервьюера
   * @param s - сессия, для которой оставляется комментарий
   * @param text - текст комментария
   */
  interviewerComment(s: OnlineSession, text: string): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'interviewer_comment')
    let body = JSON.stringify({
      comment: {
        text: text
      }
    })
    return this.appHttp.post(url, body).map((response) => this.processResponse(response))
  }

  /**
   * Удаляет вложение
   * @param s - сессия
   * @param id -  id вложения
   */
  removeAttachment(s: OnlineSession, id: number): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'remove_attachment')
    let body = JSON.stringify({
      attachment: {
        id: id
      }
    })
    return this.appHttp.patch(url, body).map((response) => this.processResponse(response))
  }

  /**
   * Добавляет к сессии вложения
   * @param s - сессия, к которой прикрепляется вложение
   * @param fileUrl - url вложения в локалной fs
   */
  updateAttachments(s: OnlineSession, fileUrl: string): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'update_attachments')
    let body = JSON.stringify({
      attachment: {
        upload_file_url: fileUrl
      }
    })
    return this.appHttp.patch(url, body).map((response) => this.processResponse(response))
  }

  /**
   * Отменяет сессию. Принимает сессию, которую надо отменить, с причиной и опциональным комментарием
   */
  cancelSession(s: OnlineSession, reason_id: number, sms: ISmsSendSign, comment?: string): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'cancel')
    let body = JSON.stringify({ reason_id, comment, sms })
    return this.appHttp.patch(url, body).map((response) => this.processResponse(response))
  }

  /**
   * Отменяет интервью. Принимает сессию, интервью которой надо отменить, с причиной и опциональным комментарием
   */
  cancelSessionInterview(s: OnlineSession, reason_id: number, comment?: string): Observable<Object> {
    let url = this.buildUrl(s.uuid, 'cancel_interview_registration')
    let body = JSON.stringify({ reason_id, comment })
    return this.appHttp.patch(url, body).map((response) => this.processResponse(response))
  }
  /**
   * Отменяет инструктаж. Принимает сессию, инструктаж который надо отменить
   */
  cancelSessionBriefing(s: OnlineSession): Observable<Object>{
    let url = this.buildUrl(s.uuid, 'cancel_briefing_registration')
    return this.appHttp.get(url).map((response) => this.processResponse(response))
  }

  /**
   * Возвращает список доступных интервьюеров, на которых можно перенести сессию
   */
  availableInterviewers(s: OnlineSession): Observable<User[]> {
    const url = this.buildUrl(s.uuid, 'available_interviewers')

    return this.appHttp.get(url).map(resp => {
      const usersJson = resp.json()["users"] as Object[]
      return usersJson.map(userJson => UserSerializer.deserialize(userJson))
    })
  }

  getPersonalData(sessionUuid: string | number, type: 'phone' | 'driver_license') {
    let url = this.buildUrl(sessionUuid, type)
    return this.get(url)
      .map(resp => resp['value']);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/models/online-session.service.ts